import { type FC, useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useGenerateRunReportDownloadUrls } from "@via/pages/projects/runs/runs.api.tsx";
import { GenericError } from "@via/components/error.tsx";
import { SkeletonLoader, Spinner } from "@via/components/loading/loading.tsx";
import { EMPTY_TABLE_MESSAGE, RunTabs } from "@via/utils/constants.tsx";
import { type SelectOption } from "@via/utils/interfaces.tsx";
import {
  DataConnectionCredentials,
  DataSourceProvider,
  type GetRunDetailsResponseBody,
  type S3ConnectionCredentials,
  type GCSConnectionCredentials,
  type GetFileListFromSSHRequestBody,
  trimAndRemoveTrailingSlash,
} from "common";
import { classNames, formatFileSize } from "@via/utils/helpers.tsx";
import FileList from "@via/pages/data/data-sources/cloud-data-sources/common/file-browser/file-list.tsx";
import Cart from "@via/pages/data/data-sources/cloud-data-sources/common/file-browser/cart.tsx";
import {
  useAmazonS3FolderList,
  useGoogleCloudFolderList,
  useSSHFolderList,
} from "@via/pages/data/data-sources/data-source.api.tsx";
import {
  BucketObject,
  keyOf,
  formatBucketObjects,
  getParentDirectory,
  FileBrowserBadge,
} from "@via/pages/data/data-sources/utils/helpers.tsx";
import { filterFilesByGlob } from "@via/pages/data/data-sources/cloud-data-sources/common/file-browser/file-browser.tsx";
import {
  HomeIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline";
import DownloadProgressModal from "@via/pages/projects/runs/downloads/download-progress-modal.tsx";
import { type RunReportDownloadUrlResponse } from "common";
import { EmptyDataIcon } from "@via/utils/custom-icons.tsx";

export enum AdditionalArea {
  Filters = "filters",
}

interface RunDownloadsProps {
  runSettingsData: GetRunDetailsResponseBody;
  isRunSettingsLoading: boolean;
  isRunSettingsError: boolean;
  selectedRunAttempt?: SelectOption;
  activeRunTab?: RunTabs;
  onDownloadStateChange?: (isRunDownloading: boolean) => void;
}

const RunDownloads: FC<RunDownloadsProps> = ({
  runSettingsData,
  isRunSettingsLoading,
  isRunSettingsError,
  activeRunTab,
  onDownloadStateChange,
}) => {
  const { runId = "" } = useParams();
  const [currentPath, setCurrentPath] = useState("");
  const [fileObjects, setFileObjects] = useState<BucketObject[]>([]);
  const [connectionCredentials, setConnectionCredentials] = useState<
    | S3ConnectionCredentials
    | GCSConnectionCredentials
    | GetFileListFromSSHRequestBody
    | null
  >(null);
  const [dataSourceProvider, setDataSourceProvider] =
    useState<DataSourceProvider | null>(null);
  const [basePath, setBasePath] = useState<string>(""); // Store base path for constructing full URLs
  const [runFolderPath, setRunFolderPath] = useState<string>(""); // Optional: /run{runId} root (may be hidden if different bucket/host)
  const resolvedReportPath =
    runSettingsData?.advancedSettings?.publishDir?.resolvedReportPath?.trim() ??
    "";

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [cart, setCart] = useState<BucketObject[]>([]);
  const [hasParentRowChecked, setHasParentRowChecked] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [additionalArea, setAdditionalArea] = useState<AdditionalArea | null>(
    null
  );
  const [filterValue, setFilterValue] = useState("");
  const [downloadUrls, setDownloadUrls] = useState<
    RunReportDownloadUrlResponse[]
  >([]);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const {
    mutateAsync: generateRunReportDownloadUrls,
    isPending: isGeneratingUrls,
  } = useGenerateRunReportDownloadUrls();

  // Get the appropriate folder list hook
  const { mutateAsync: getAmazonFolderList, isPending: isAmazonLoading } =
    useAmazonS3FolderList();
  const { mutateAsync: getGoogleFolderList, isPending: isGoogleLoading } =
    useGoogleCloudFolderList();
  const { mutateAsync: getSSHFolderList, isPending: isSSHLoading } =
    useSSHFolderList();

  const isGetFolderListLoading =
    isAmazonLoading || isGoogleLoading || isSSHLoading;

  // Determine whether to show Recursive Filter button
  const showRecursiveFilter = useMemo(() => {
    return Array.from(selectedKeys).some(key => key.endsWith("/"));
  }, [selectedKeys]);

  // Reset additional area when recursive filter is not shown
  useEffect(() => {
    if (!showRecursiveFilter) {
      setAdditionalArea(null);
    }
  }, [showRecursiveFilter]);

  // Reset filter when additional area changes
  useEffect(() => {
    if (additionalArea !== AdditionalArea.Filters) {
      setFilterValue("");
    }
  }, [additionalArea]);

  // Determine storage provider and build connection credentials
  useEffect(() => {
    if (!runSettingsData || activeRunTab !== RunTabs.Downloads) return;

    const runEnvironment = runSettingsData.runEnvironment;
    const publishDir = runSettingsData.advancedSettings?.publishDir;
    const workDir = runSettingsData.advancedSettings?.workDir;

    // Find the selected environment from the available list
    const selectedEnv = runEnvironment?.availableList?.find(
      env => env.id === runEnvironment.selectedId
    );

    if (!selectedEnv) {
      console.warn("No selected environment found");
      return;
    }

    const fullReportPath = trimAndRemoveTrailingSlash(
      publishDir?.resolvedReportPath
    );
    if (!fullReportPath) {
      // Treat missing resolvedReportPath as "Downloads not available".
      setDataSourceProvider(null);
      setConnectionCredentials(null);
      setBasePath("");
      setRunFolderPath("");
      setCurrentPath("");
      setFileObjects([]);
      return;
    }

    const resolvedWorkPath = trimAndRemoveTrailingSlash(
      workDir?.resolvedWorkPath
    );
    const runRootCandidate = resolvedWorkPath.endsWith("/work")
      ? resolvedWorkPath.slice(0, -"/work".length)
      : resolvedWorkPath;

    // Detect provider based on publish dir path
    const isGcsPath = fullReportPath.startsWith("gs://");
    const isS3Path = fullReportPath.startsWith("s3://");

    //there is a chance that the run root is not the same as the report root. In that case, we don't want to include the run root just use the report root.
    const shouldIncludeRunRoot = (): boolean => {
      if (!runRootCandidate) return false;

      if (isGcsPath) {
        const reportBucket = fullReportPath.match(/^gs:\/\/([^/]+)\//)?.[1];
        const runBucket = runRootCandidate.match(/^gs:\/\/([^/]+)\//)?.[1];
        return !!reportBucket && reportBucket === runBucket;
      }

      if (isS3Path) {
        const reportBucket = fullReportPath.match(/^s3:\/\/([^/]+)\//)?.[1];
        const runBucket = runRootCandidate.match(/^s3:\/\/([^/]+)\//)?.[1];
        return !!reportBucket && reportBucket === runBucket;
      }

      return false;
    };

    setCurrentPath("");
    setRunFolderPath(shouldIncludeRunRoot() ? runRootCandidate : "");

    if (isGcsPath) {
      // Google Cloud Storage
      setDataSourceProvider(DataSourceProvider.Google);

      // Parse bucket and prefix from path (e.g., gs://bucket-name/path/report123)
      const match = fullReportPath.match(/^gs:\/\/([^/]+)(?:\/(.*))?$/);
      if (match) {
        const [, bucketName, prefix] = match;
        setBasePath(fullReportPath); // Store full report path as base

        // Use saved credential if available, otherwise use -1 for IAM Role authentication
        setConnectionCredentials({
          savedCredentialId: selectedEnv.googleCreId || -1,
          bucketName,
          prefix: prefix || undefined,
        } as GCSConnectionCredentials);
      }
    } else if (isS3Path) {
      // Amazon S3
      setDataSourceProvider(DataSourceProvider.Amazon);

      // Parse bucket and prefix from path (e.g., s3://bucket-name/path/report123)
      const match = fullReportPath.match(/^s3:\/\/([^/]+)(?:\/(.*))?$/);
      if (match) {
        const [, bucketName, prefix] = match;
        setBasePath(fullReportPath); // Store full report path as base

        // Use saved credential if available, otherwise use -1 for IAM Role authentication
        setConnectionCredentials({
          savedCredentialId: selectedEnv.amazonCreId || -1,
          bucketName,
          prefix: prefix || undefined,
        } as S3ConnectionCredentials);
      }
    } else {
      // Other paths - use SSH
      setDataSourceProvider(DataSourceProvider.SSH);
      setBasePath(fullReportPath); // Store full SSH path

      setConnectionCredentials({
        dir: fullReportPath,
        runEnvId: selectedEnv.id,
        showHidden: false,
        recursive: false,
      } as GetFileListFromSSHRequestBody);
    }
  }, [runSettingsData, activeRunTab, runId]);

  const fetchFileList = useCallback(async () => {
    if (!connectionCredentials || !dataSourceProvider) return;

    const isAtRoot = !currentPath;

    try {
      let reportResponse;
      let runResponse;

      switch (dataSourceProvider) {
        case DataSourceProvider.Amazon: {
          const reportParams: S3ConnectionCredentials = {
            ...connectionCredentials,
          } as S3ConnectionCredentials;
          if (currentPath) {
            reportParams.prefix = currentPath;
          }

          // At root, fetch both folders in parallel
          if (isAtRoot && runFolderPath) {
            const runPrefix = runFolderPath.match(/^s3:\/\/[^/]+\/(.+)$/)?.[1];
            if (runPrefix) {
              const runParams: S3ConnectionCredentials = {
                ...connectionCredentials,
                prefix: runPrefix,
              } as S3ConnectionCredentials;
              [reportResponse, runResponse] = await Promise.all([
                getAmazonFolderList(reportParams),
                getAmazonFolderList(runParams),
              ]);
            } else {
              // No valid run prefix, just fetch report
              reportResponse = await getAmazonFolderList(reportParams);
            }
          } else {
            // Not at root, just fetch report
            reportResponse = await getAmazonFolderList(reportParams);
          }
          break;
        }

        case DataSourceProvider.Google: {
          const reportParams: GCSConnectionCredentials = {
            ...connectionCredentials,
          } as GCSConnectionCredentials;
          if (currentPath) {
            reportParams.prefix = currentPath;
          }

          // At root, fetch both folders in parallel
          if (isAtRoot && runFolderPath) {
            const runPrefix = runFolderPath.match(/^gs:\/\/[^/]+\/(.+)$/)?.[1];
            if (runPrefix) {
              const runParams: GCSConnectionCredentials = {
                ...connectionCredentials,
                prefix: runPrefix,
              } as GCSConnectionCredentials;
              [reportResponse, runResponse] = await Promise.all([
                getGoogleFolderList(reportParams),
                getGoogleFolderList(runParams),
              ]);
            } else {
              // No valid run prefix, just fetch report
              reportResponse = await getGoogleFolderList(reportParams);
            }
          } else {
            // Not at root, just fetch report
            reportResponse = await getGoogleFolderList(reportParams);
          }
          break;
        }

        case DataSourceProvider.SSH: {
          const sshCreds =
            connectionCredentials as GetFileListFromSSHRequestBody;
          const baseDir = sshCreds.dir;
          let targetDir = currentPath || baseDir;

          // Remove trailing slash if present for SSH directories
          if (targetDir && targetDir.endsWith("/")) {
            targetDir = targetDir.slice(0, -1);
          }

          const reportParams: DataConnectionCredentials = {
            savedCredentialId: sshCreds.runEnvId,
            bucketName: targetDir,
            recursive: false,
          };

          // At root, fetch both folders in parallel
          if (isAtRoot && runFolderPath) {
            const runParams: DataConnectionCredentials = {
              savedCredentialId: sshCreds.runEnvId,
              bucketName: runFolderPath,
              recursive: false,
            };
            [reportResponse, runResponse] = await Promise.all([
              getSSHFolderList(reportParams),
              getSSHFolderList(runParams),
            ]);
          } else {
            // Not at root, just fetch report
            reportResponse = await getSSHFolderList(reportParams);
          }
          break;
        }

        default:
          return;
      }

      // Format responses to BucketObject[] and merge both folders
      const reportFiles = formatBucketObjects(
        reportResponse,
        dataSourceProvider
      );
      const runFiles = runResponse
        ? formatBucketObjects(runResponse, dataSourceProvider)
        : [];
      setFileObjects([...reportFiles, ...runFiles]);
    } catch (error) {
      console.error("Error fetching file list:", error);
      setFileObjects([]);
    }
  }, [connectionCredentials, dataSourceProvider, currentPath, runFolderPath]);

  // Fetch file list when credentials, provider, or path changes
  useEffect(() => {
    if (!connectionCredentials || !dataSourceProvider) return;

    fetchFileList();
  }, [connectionCredentials, dataSourceProvider, currentPath, fetchFileList]);

  const filteredFiles = useMemo(() => {
    const term = searchInput.trim();
    if (!term) return fileObjects;
    return filterFilesByGlob(fileObjects, term, true);
  }, [searchInput, fileObjects]);

  const objectsMemo = useMemo(
    () => filteredFiles.filter(obj => obj.id !== ".."),
    [filteredFiles]
  );

  useEffect(() => {
    clearSelection();
    setSearchInput("");
  }, [fileObjects]);

  // Get relative path from base directory (for display purposes)
  const getRelativePath = (fullPath: string): string => {
    if (!fullPath || !basePath) return "";

    // Remove base path from the full path to get relative path
    // Handles both report and run folder paths:
    // - SSH: "/home/vfoundry/tmp/report12640" or "/home/vfoundry/tmp/run12640"
    // - S3: "s3://bucket/dev-playground/runs/report12640" or "s3://bucket/dev-playground/runs/run12640"
    // - GCS: "gs://bucket/dev-playground/runs/report12640" or "gs://bucket/dev-playground/runs/run12640"
    const normalizedReportBase = basePath.replace(/\/$/, "");
    const normalizedRunBase = runFolderPath
      ? runFolderPath.replace(/\/$/, "")
      : "";
    const normalizedFull = fullPath.replace(/\/$/, "");

    // Check report folder first
    if (normalizedFull === normalizedReportBase) return "";
    if (normalizedFull.startsWith(normalizedReportBase + "/")) {
      return normalizedFull.substring(normalizedReportBase.length + 1);
    }

    // Check run folder (optional)
    if (normalizedRunBase) {
      if (normalizedFull === normalizedRunBase) return "";
      if (normalizedFull.startsWith(normalizedRunBase + "/")) {
        return normalizedFull.substring(normalizedRunBase.length + 1);
      }
    }

    return fullPath;
  };

  const handleFolderClick = (folder: BucketObject) => {
    if (folder.id === "..") {
      // Go to parent directory
      const newPath = getParentDirectory(currentPath);

      // For S3/GCS, currentPath is just the prefix (e.g., "dev-playground/runs/report123/")
      // basePath is the full path (e.g., "s3://bucket/dev-playground/runs/report123")
      // We need to extract just the prefix from basePath for comparison
      let reportBasePrefix = basePath;
      let runBasePrefix = runFolderPath;
      if (
        dataSourceProvider === DataSourceProvider.Amazon ||
        dataSourceProvider === DataSourceProvider.Google
      ) {
        // Extract prefix from basePath (e.g., "s3://bucket/prefix" -> "prefix")
        const reportMatch = basePath.match(/^(?:s3|gs):\/\/[^/]+\/(.+)$/);
        reportBasePrefix = reportMatch ? reportMatch[1] : "";
        if (runFolderPath) {
          const runMatch = runFolderPath.match(/^(?:s3|gs):\/\/[^/]+\/(.+)$/);
          runBasePrefix = runMatch ? runMatch[1] : "";
        } else {
          runBasePrefix = "";
        }
      }

      const normalizedReportBase = reportBasePrefix.replace(/\/$/, "");
      const normalizedRunBase = runBasePrefix
        ? runBasePrefix.replace(/\/$/, "")
        : "";
      const normalizedNew = (newPath || "").replace(/\/$/, "");

      // Check if we're already at home (empty currentPath)
      if (!currentPath) {
        return;
      }

      // Check if new path would go above both report and run base folders
      const isWithinReport =
        normalizedNew && normalizedNew.startsWith(normalizedReportBase);
      const isWithinRun =
        normalizedRunBase &&
        normalizedNew &&
        normalizedNew.startsWith(normalizedRunBase);

      if (!normalizedNew || (!isWithinReport && !isWithinRun)) {
        // Navigate to home (empty string)
        setCurrentPath("");
        return;
      }

      setCurrentPath(newPath || "");
    } else if (folder.type === "folder") {
      let folderPath = folder.name || "";
      // For SSH, ensure the path starts with / if it's missing
      if (
        dataSourceProvider === DataSourceProvider.SSH &&
        folderPath &&
        !folderPath.startsWith("/")
      ) {
        folderPath = `/${folderPath}`;
      }
      setCurrentPath(folderPath);
    }
  };

  const handleHomeClick = () => {
    setCurrentPath("");
  };

  const toggleSelect = (item: BucketObject) => {
    const k = keyOf(item);
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const selectAllFiles = () => {
    setSelectedKeys(new Set(objectsMemo.map(keyOf)));
  };

  const clearSelection = () => {
    setSelectedKeys(new Set());
    setHasParentRowChecked(false);
  };

  // Recursively get all files from a folder
  const getExtraFolderObjects = async (folder: BucketObject) => {
    if (!connectionCredentials || !dataSourceProvider) return [];

    try {
      let response;

      switch (dataSourceProvider) {
        case DataSourceProvider.Amazon: {
          const s3Params: S3ConnectionCredentials = {
            ...connectionCredentials,
            prefix: folder.name,
            recursive: true,
          } as S3ConnectionCredentials;
          response = await getAmazonFolderList(s3Params);
          break;
        }

        case DataSourceProvider.Google: {
          const gcsParams: GCSConnectionCredentials = {
            ...connectionCredentials,
            prefix: folder.name,
            recursive: true,
          } as GCSConnectionCredentials;
          response = await getGoogleFolderList(gcsParams);
          break;
        }

        case DataSourceProvider.SSH: {
          const sshCreds =
            connectionCredentials as GetFileListFromSSHRequestBody;
          let folderPath = folder.name || "";

          // For SSH, ensure the path starts with / if it's missing
          if (folderPath && !folderPath.startsWith("/")) {
            folderPath = `/${folderPath}`;
          }

          // Remove trailing slash if present
          if (folderPath.endsWith("/")) {
            folderPath = folderPath.slice(0, -1);
          }

          const sshParams: DataConnectionCredentials = {
            savedCredentialId: sshCreds.runEnvId,
            bucketName: folderPath,
            recursive: true,
          };
          response = await getSSHFolderList(sshParams);
          break;
        }

        default:
          return [];
      }

      return response;
    } catch (error) {
      console.error("Error fetching folder contents:", error);
      return [];
    }
  };

  // Add selected files to cart with recursive folder support
  const handleAddSelectedToCart = async () => {
    if (selectedKeys.size === 0) return;

    const selectedMap = new Set(selectedKeys);
    const existing = [...cart];
    const newCandidates: BucketObject[] = [];

    for (const f of filteredFiles) {
      const k = keyOf(f);
      if (selectedMap.has(k) && !existing.some(x => keyOf(x) === k)) {
        newCandidates.push(f);
      }
    }

    // Handle folder selection - get all files recursively
    for (const item of newCandidates) {
      if (item.type === "folder") {
        const extraFolderObjects = await getExtraFolderObjects(item);
        const formattedExtraObjects = formatBucketObjects(
          extraFolderObjects,
          dataSourceProvider!
        );

        // Only add brand-new ones (files only, not folders)
        const extraNewItems = formattedExtraObjects.filter(
          extraItem =>
            extraItem.type !== "folder" &&
            !existing.some(x => keyOf(x) === keyOf(extraItem)) &&
            !newCandidates.some(x => keyOf(x) === keyOf(extraItem))
        );

        newCandidates.push(...extraNewItems);
      }
    }

    // Filter to files only
    const filesOnly = newCandidates.filter(item => item.type !== "folder");

    // Apply recursive filter pattern if set
    const filteredNew = filterFilesByGlob(filesOnly, filterValue);

    // Merge old + new
    setCart([...existing, ...filteredNew]);
    clearSelection();
  };

  const handleAdditionalAreaButtonClick = (value: AdditionalArea | null) => {
    if (value === additionalArea) {
      setAdditionalArea(null);
    } else {
      setAdditionalArea(value);
    }
  };

  const removeFromCart = (key: string) => {
    setCart(prev => prev.filter(item => keyOf(item) !== key));
  };

  const clearCart = (completedFileNames?: string[]) => {
    if (completedFileNames && completedFileNames.length > 0) {
      // Partial cleanup: Remove only completed files from cart
      setCart(prev =>
        prev.filter(item => {
          const itemName = item.displayName || item.name;
          // Keep items without names to handle edge cases robustly
          if (!itemName) return true;
          return !completedFileNames.includes(itemName);
        })
      );
    } else {
      // Full cleanup: Clear entire cart
      setCart([]);
    }
  };

  // Helper function to compute prefixed filename from path
  // e.g., "run12524/exper_rep1.txt" -> "run12524_exper_rep1.txt"
  // e.g., "report12345/multiqc/multiqc.html" -> "report12345_multiqc_multiqc.html"
  const getPrefixedFilename = (path: string, originalName: string): string => {
    // Extract the full path including report{id}/ or run{id}/ prefix
    const match = path.match(/((?:report|run)\d+\/.+)$/);
    if (match) {
      const fullRelativePath = match[1];
      // Replace directory separators with underscores
      return fullRelativePath.replace(/\//g, "_");
    }
    return originalName;
  };

  // Prepare files for download URL generation
  const prepareFilesForDownload = (usePrefixedFilenames: boolean) => {
    return cart.map(item => {
      // Construct full path based on provider
      let fullPath = item.name;

      if (
        dataSourceProvider === DataSourceProvider.Google ||
        dataSourceProvider === DataSourceProvider.Amazon
      ) {
        if (!fullPath.startsWith("gs://") && !fullPath.startsWith("s3://")) {
          const match = basePath.match(/^((?:s3|gs):\/\/[^/]+)\//);
          if (match) {
            const bucketUrl = match[1];
            fullPath = `${bucketUrl}/${fullPath}`;
          }
        }
      } else if (dataSourceProvider === DataSourceProvider.SSH) {
        const basePathWithoutSlash = basePath.replace(/^\//, "");
        if (fullPath.startsWith("/")) {
          // Already absolute path
        } else if (fullPath.startsWith(basePathWithoutSlash)) {
          fullPath = `/${fullPath}`;
        } else {
          fullPath = `${basePath}/${fullPath}`;
        }
      }

      const originalName = item.displayName || item.name;
      const fileName = usePrefixedFilenames
        ? getPrefixedFilename(fullPath, originalName)
        : originalName;

      return {
        name: fileName,
        path: fullPath,
        size: Number(item.size) || 0,
      };
    });
  };

  const handleContinue = async () => {
    if (cart.length === 0) return;

    // Prepare files with prefix initially (checkbox starts checked)
    const files = prepareFilesForDownload(true);

    // Generate download URLs for browser downloads (with Content-Disposition)
    const result = await generateRunReportDownloadUrls({
      runId,
      files,
      downloadMode: "browser",
    });

    if (result) {
      setDownloadUrls(result.urls);
      setShowDownloadModal(true);
    }
  };

  // Callback to regenerate URLs with prefixed filenames
  const handleRegenerateUrls = async (usePrefixedFilenames: boolean) => {
    if (cart.length === 0) return;

    const files = prepareFilesForDownload(usePrefixedFilenames);
    const result = await generateRunReportDownloadUrls({
      runId,
      files,
      downloadMode: "browser",
    });

    if (result) {
      setDownloadUrls(result.urls);
    }
  };

  // Callback to generate script-specific URLs (without Content-Disposition for clean pre-signed URLs)
  const handleGenerateScriptUrls = async (usePrefixedFilenames: boolean) => {
    if (cart.length === 0) return null;

    const files = prepareFilesForDownload(usePrefixedFilenames);
    const result = await generateRunReportDownloadUrls({
      runId,
      files,
      downloadMode: "script",
    });
    return result?.urls ?? null;
  };

  const handleCloseDownloadModal = () => {
    setShowDownloadModal(false);
    setDownloadUrls([]);
  };

  const getTotalSize = (): number => {
    return cart.reduce((acc, item) => acc + (Number(item.size) || 0), 0);
  };

  if (isRunSettingsLoading) {
    return (
      <main className="pt-4">
        <div className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
          <SkeletonLoader count={12} />
        </div>
      </main>
    );
  }

  if (isRunSettingsError) {
    return (
      <main className="pt-4">
        <div className="via-generic-error w-full justify-center">
          <GenericError message={EMPTY_TABLE_MESSAGE.DOWNLOAD_REPORTS} />
        </div>
      </main>
    );
  }

  if (!resolvedReportPath) {
    return (
      <main className="pt-4">
        <div className="via-generic-error w-full justify-center">
          <EmptyDataIcon />
          <p>Downloads are not available for this run.</p>
        </div>
      </main>
    );
  }

  if (
    !dataSourceProvider ||
    !connectionCredentials ||
    (!isGetFolderListLoading && fileObjects.length === 0 && !currentPath)
  ) {
    return (
      <main className="pt-4">
        <div className="via-generic-error w-full justify-center">
          <EmptyDataIcon />
          <p>{EMPTY_TABLE_MESSAGE.DOWNLOAD_REPORTS}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-4">
      <div className="mx-auto grid grid-cols-1 gap-6 pb-4 md:grid-cols-[1fr_480px]">
        {/* File Browser Section */}
        <div className="flex flex-col gap-4">
          <div>
            <h2 className="via-h2 mb-2">Browse Files</h2>
            <p className="text-sm text-gray-600">
              Browse files from report directory (and run directory when
              available).
            </p>
          </div>

          {/* Breadcrumb / Path Navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleHomeClick}
              className="inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              title="Go to reports directory">
              <HomeIcon className="h-4 w-4" />
            </button>
            {currentPath && getRelativePath(currentPath) && (
              <span className="text-sm text-gray-600">
                /{getRelativePath(currentPath)}
              </span>
            )}
          </div>

          {/* Search Bar */}
          {isGetFolderListLoading ? (
            <SkeletonLoader count={1} />
          ) : (
            <div>
              <div className="flex-grow">
                <div className="relative flex items-center">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 h-4 w-4 text-gray-700" />
                  <input
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    type="text"
                    placeholder="Type to filter... e.g., *.fastq.gz, sample1"
                    className="w-full rounded-md border-0 py-2 pl-9 pr-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Action Bar */}
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-gray-700">Files</div>
            <div className="flex items-center gap-2">
              {showRecursiveFilter && (
                <button
                  data-testid="fileFilterButton"
                  onClick={() =>
                    handleAdditionalAreaButtonClick(AdditionalArea.Filters)
                  }
                  className={classNames(
                    "flex items-center space-x-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-100 transition",
                    additionalArea === AdditionalArea.Filters
                      ? "bg-gray-200"
                      : ""
                  )}>
                  <FunnelIcon className="h-5 w-5 text-gray-700" />
                  <span>Recursive Filter</span>
                </button>
              )}
              <button
                data-testid="addToCartButton"
                onClick={handleAddSelectedToCart}
                disabled={selectedKeys.size === 0 || isGetFolderListLoading}
                className="inline-flex items-center rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-50">
                <Spinner loading={isGetFolderListLoading}>
                  Add to Cart <FileBrowserBadge count={selectedKeys.size} />
                </Spinner>
              </button>
            </div>
          </div>

          {/* Recursive Filter UI */}
          {additionalArea === AdditionalArea.Filters && (
            <div className="rounded-lg border bg-blue-50/50 px-3 py-4">
              <div className="text-gray-700 text-sm font-bold mb-2">
                Include Pattern
              </div>
              <input
                data-testid="patternFilterInput"
                type="text"
                value={filterValue}
                onChange={e => setFilterValue(e.target.value)}
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                placeholder="e.g., *.json, data*, config*"
              />
              <div className="text-xs font-semibold text-gray-400 mt-2">
                Include Pattern applies filters to the selected items (files or
                folders). Only the files matching the filter within the selected
                items will be added to the cart.
              </div>
              <div className="text-xs font-semibold text-gray-400 mt-2">
                Use * as wildcard and ? for a single character (e.g., *.fastq
                for fastq extension files, data* for files starting with
                `data`).
              </div>
            </div>
          )}

          {/* File List */}
          <div>
            <FileList
              dataSourceProvider={dataSourceProvider}
              items={filteredFiles}
              selectedKeys={selectedKeys}
              onToggleSelect={toggleSelect}
              onClickSelected={handleFolderClick}
              onSelectAll={selectAllFiles}
              onClearSelection={clearSelection}
              isGetFolderListLoading={isGetFolderListLoading}
              disableFolderSelection={false}
              hasParentRowChecked={hasParentRowChecked}
              setHasParentRowChecked={setHasParentRowChecked}
            />
          </div>
        </div>

        {/* Cart Section */}
        <div className="flex flex-col gap-2">
          <div>
            <h2 className="via-h2 mb-2">Download Cart</h2>
            {cart.length > 0 && (
              <div className="text-sm text-gray-600">
                Total size: {formatFileSize(getTotalSize())}
              </div>
            )}
          </div>
          <Cart
            items={cart}
            onRemove={removeFromCart}
            onClear={clearCart}
            onContinue={handleContinue}
            submitButtonText="Download"
            submitButtonLoading={isGeneratingUrls}
          />
        </div>
      </div>

      {/* Download Progress Modal */}
      <DownloadProgressModal
        open={showDownloadModal}
        onClose={handleCloseDownloadModal}
        downloadUrls={downloadUrls}
        onDownloadComplete={clearCart}
        onDownloadStateChange={onDownloadStateChange}
        onRegenerateUrls={handleRegenerateUrls}
        onGenerateScriptUrls={handleGenerateScriptUrls}
        isRegeneratingUrls={isGeneratingUrls}
      />
    </main>
  );
};

export default RunDownloads;
